import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, TextField, Alert, Checkbox, ListItemText, Divider, SelectChangeEvent } from '@mui/material';
import { Rocket, RefreshCw, Clock, Target, BarChart3, CheckCircle2, XCircle, Circle } from 'lucide-react';
import { PageHeader, LoadingState, ErrorState, MetricCard } from './shared/components';
import { useMLData, usePolling } from './shared/hooks';
import { mlApiClient, formatters } from './shared/utils';

/**
 * Training Pipeline - Platform Admin
 * 
 * PLATFORM-LEVEL FEATURE:
 * - Monitor ML model training pipeline health
 * - Track model loading status
 * - Performance metrics for training jobs
 * - Queue depth monitoring
 * 
 * VISION ALIGNMENT: Cybersecurity Autopilot
 * - Monitor autonomous AI training pipeline
 * - Ensure models are loaded and ready
 * - Track training performance and errors
 * 
 * Following 00-READ-THIS-FIRST:
 * - PROGRESSIVE COMPLEXITY: Start simple, can enhance later
 * - EVIDENCE-FIRST: Backend API exists at /api/ml/pipeline/health
 * - NO MOCK DATA: All data from real backend
 */

interface PipelineHealth {
  status: 'healthy' | 'degraded' | 'down';
  tenant_id: string;
  models_loaded: number;
  queue_depth: number;
  avg_latency_ms: number;
  error_rate: number;
  last_prediction: string | null;
  timestamp: string;
  healthy: boolean;
}

interface TrainingStatus {
  tenant_id: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress_percentage: number;
  current_model: string | null;
  models_completed: number;
  total_models: number;
  datasets_total?: number;
  datasets_completed?: number;
  current_dataset?: string | null;
  start_time: string | null;
  errors: string[];
}

interface AlgorithmMetrics {
  accuracy?: number;
  precision?: number;
  recall?: number;
  f1_score?: number;
  [key: string]: any;
}

interface AlgorithmResult {
  status: 'success' | 'failed';
  metrics?: AlgorithmMetrics;
  error?: string;
  timestamp: string;
}

interface TrainingResults {
  tenant_id: string;
  training_results: Record<string, AlgorithmResult>;
  summary: {
    total_algorithms: number;
    successful: number;
    failed: number;
    success_rate: number;
  };
  last_updated?: string;
  completed_at?: string;
}

interface Tenant {
  id: string;
  name: string;
  status: string;
}

export const TrainingPipeline: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [datasetTypes, setDatasetTypes] = useState<string[]>(['synthetic']);
  const [sampleSize, setSampleSize] = useState(10000);
  const [training, setTraining] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState<TrainingStatus | null>(null);
  const [trainingResults, setTrainingResults] = useState<TrainingResults | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ML-009: Pipeline health monitoring enabled (authentication verified)
  const { data: pipelineHealth, loading, error, refresh } = useMLData<PipelineHealth>(
    () => mlApiClient.getPipelineHealth() as Promise<PipelineHealth>,
    { pollingInterval: 30000 }
  );

  // ML-011: Load tenants dynamically from backend
  useEffect(() => {
    const loadTenants = async () => {
      setLoadingTenants(true);
      try {
        const response = await fetch('/api/platform-admin/tenants', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('astrapeak_platform_admin_token') || ''}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const tenantList = data.tenants || [];
          setTenants(tenantList);
          
          // Set default tenant to first in list, or AstraPeak if it exists
          if (tenantList.length > 0) {
            const astrapeakTenant = tenantList.find(
              (t: Tenant) => t.id === 'd6f953f4-d914-4906-b61e-d48d6b975f74'
            );
            setSelectedTenant(astrapeakTenant ? astrapeakTenant.id : tenantList[0].id);
          }
        } else {
          console.error('Failed to load tenants:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
      } finally {
        setLoadingTenants(false);
      }
    };
    
    loadTenants();
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleSelectAllDatasets = () => {
    if (datasetTypes.length === 3) {
      setDatasetTypes(['synthetic']);
    } else {
      setDatasetTypes(['synthetic', 'cicids2017', 'unsw-nb15']);
    }
  };

  const handleDatasetChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setDatasetTypes(value.length > 0 ? value : ['synthetic']);
  };

  const handleTrainAllModels = async () => {
    setTraining(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const result = await mlApiClient.trainAllModels(selectedTenant, datasetTypes, sampleSize) as any;
      if (result.success) {
        const datasetsText = result.datasets_count > 1 ? `${result.datasets_count} datasets` : '1 dataset';
        setSuccessMessage(`Training started for ${result.total_models} models across ${datasetsText}. Estimated time: ${result.estimated_duration_minutes} minutes`);
        
        // Initialize training status to show progress immediately
        setTrainingStatus({
          tenant_id: selectedTenant,
          status: 'running',
          progress_percentage: 0,
          current_model: null,
          models_completed: 0,
          total_models: result.total_models,
          datasets_total: result.datasets_count,
          datasets_completed: 0,
          current_dataset: datasetTypes[0],
          start_time: new Date().toISOString(),
          errors: []
        });
        
        // Start polling for status
        startStatusPolling();
      }
    } catch (err: any) {
      setErrorMessage(`Failed to start model training: ${err.message || 'Unknown error'}`);
    } finally {
      setTraining(false);
    }
  };

  const fetchTrainingResults = async () => {
    try {
      const response = await fetch(`/api/platform-admin/ml/training/results/${selectedTenant}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('astrapeak_platform_admin_token') || ''}`,
        },
      });
      
      if (response.ok) {
        const results = await response.json() as TrainingResults;
        setTrainingResults(results);
      } else if (response.status === 404) {
        console.log('No detailed training results available (older training run)');
      }
    } catch (err) {
      console.error('Failed to fetch training results:', err);
    }
  };

  const startStatusPolling = () => {
    // Clear any existing interval before starting a new one
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await mlApiClient.getTrainingStatus(selectedTenant) as TrainingStatus;
        setTrainingStatus(status);
        
        // Stop polling if training is complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (status.status === 'completed') {
            setSuccessMessage(`Training completed! ${status.models_completed}/${status.total_models} models trained successfully.`);
            // Fetch detailed per-algorithm results (M-004)
            fetchTrainingResults();
          } else {
            setErrorMessage(`Training failed: ${status.errors.join(', ')}`);
          }
        }
      } catch (err) {
        console.error('Failed to fetch training status:', err);
      }
    }, 5000); // Poll every 5 seconds
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'down': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500/10 border-green-500/20';
      case 'degraded': return 'bg-yellow-500/10 border-yellow-500/20';
      case 'down': return 'bg-red-500/10 border-red-500/20';
      default: return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  if (loading) return <LoadingState message="Loading pipeline health..." />;
  if (error) return <ErrorState error={error} onRetry={refresh} />;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Training Pipeline"
        subtitle="Monitor ML model training pipeline health"
        onRefresh={refresh}
        loading={loading}
      />

      {/* Training Controls */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Target size={18} /> Start New Training</h2>
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FormControl fullWidth>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              label="Tenant"
              disabled={training || loadingTenants}
            >
              {loadingTenants ? (
                <MenuItem value="" disabled>Loading tenants...</MenuItem>
              ) : tenants.length === 0 ? (
                <MenuItem value="" disabled>No tenants available</MenuItem>
              ) : (
                tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Dataset Types</InputLabel>
            <Select
              multiple
              value={datasetTypes}
              onChange={handleDatasetChange}
              label="Dataset Types"
              disabled={training}
              renderValue={(selected) => 
                selected.length === 3 
                  ? 'All Datasets (3)' 
                  : `${selected.length} dataset${selected.length > 1 ? 's' : ''} selected`
              }
            >
              <MenuItem onClick={handleSelectAllDatasets}>
                <Checkbox checked={datasetTypes.length === 3} />
                <ListItemText primary="Select All Datasets" />
              </MenuItem>
              <Divider />
              <MenuItem value="synthetic">
                <Checkbox checked={datasetTypes.includes('synthetic')} />
                <ListItemText primary="Synthetic Data" />
              </MenuItem>
              <MenuItem value="cicids2017">
                <Checkbox checked={datasetTypes.includes('cicids2017')} />
                <ListItemText primary="CICIDS2017" />
              </MenuItem>
              <MenuItem value="unsw-nb15">
                <Checkbox checked={datasetTypes.includes('unsw-nb15')} />
                <ListItemText primary="UNSW-NB15" />
              </MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="Sample Size"
            type="number"
            value={sampleSize}
            onChange={(e) => setSampleSize(parseInt(e.target.value))}
            disabled={training}
            fullWidth
          />
        </div>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrainAllModels}
          disabled={training || trainingStatus?.status === 'running'}
          fullWidth
          sx={{ 
            mb: 2,
            background: trainingStatus?.status === 'running' 
              ? 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)' 
              : undefined
          }}
        >
          {training ? <><Clock size={16} className="inline mr-1" /> Starting Training...</> : trainingStatus?.status === 'running' ? <><RefreshCw size={16} className="inline mr-1 animate-spin" /> Training in Progress...</> : <><Rocket size={16} className="inline mr-1" /> TRAIN ALL MODELS (17 ALGORITHMS)</>}
        </Button>
        
        <div className="text-sm text-gray-400">
          Estimated time: {25 * datasetTypes.length}-{30 * datasetTypes.length} minutes ({datasetTypes.length} dataset{datasetTypes.length > 1 ? 's' : ''})
        </div>
      </div>

      {/* Training Progress */}
      {trainingStatus && trainingStatus.status !== 'idle' && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 size={18} /> Current Training Progress</h2>
          
          {/* Dataset Progress */}
          {trainingStatus.datasets_total && trainingStatus.datasets_total > 1 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Dataset Progress:</span>
                <span className="text-blue-400 font-semibold">
                  {trainingStatus.current_dataset} ({trainingStatus.datasets_completed}/{trainingStatus.datasets_total})
                </span>
              </div>
            </div>
          )}
          
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Status:</span>
              <span className={`font-semibold ${
                trainingStatus.status === 'running' ? 'text-yellow-400' :
                trainingStatus.status === 'completed' ? 'text-green-400' :
                trainingStatus.status === 'failed' ? 'text-red-400' :
                'text-gray-400'
              }`}>
                {trainingStatus.status === 'running' ? <><Circle size={12} className="inline text-yellow-400 fill-yellow-400 mr-1" />Training in progress...</> :
                 trainingStatus.status === 'completed' ? <><CheckCircle2 size={14} className="inline mr-1" />Completed</> :
                 trainingStatus.status === 'failed' ? <><XCircle size={14} className="inline mr-1" />Failed</> :
                 'Idle'}
              </span>
            </div>
            
            <div className="w-full bg-gray-700 rounded-full h-4 mb-2 overflow-hidden">
              <div
                className="h-4 rounded-full transition-all duration-500"
                style={{ 
                  width: `${trainingStatus.progress_percentage}%`,
                  background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)'
                }}
              />
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">
                Progress: {trainingStatus.progress_percentage.toFixed(1)}% ({trainingStatus.models_completed}/{trainingStatus.total_models} models)
              </span>
              {trainingStatus.current_model && (
                <span className="text-blue-400 font-semibold flex items-center gap-1"><RefreshCw size={14} className="animate-spin" /> Training: {trainingStatus.current_model}</span>
              )}
            </div>
          </div>
          
          {trainingStatus.start_time && (
            <div className="text-sm text-gray-400">
              Started: {new Date(trainingStatus.start_time).toLocaleString()}
            </div>
          )}
          
          {trainingStatus.errors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Errors: {trainingStatus.errors.join(', ')}
            </Alert>
          )}
        </div>
      )}

      {/* Per-Algorithm Training Results (M-004) */}
      {trainingResults && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 size={18} /> Per-Algorithm Training Results
            </h2>
            <div className="text-sm text-gray-400">
              Success Rate: <span className={`font-semibold ${trainingResults.summary.success_rate >= 80 ? 'text-green-400' : trainingResults.summary.success_rate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                {trainingResults.summary.success_rate.toFixed(1)}%
              </span> ({trainingResults.summary.successful}/{trainingResults.summary.total_algorithms})
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Algorithm</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Accuracy</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Precision</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Recall</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">F1 Score</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(trainingResults.training_results).map(([algorithm, result]) => (
                  <tr key={algorithm} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                    <td className="py-3 px-4 text-white font-medium">{algorithm.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                    <td className="py-3 px-4">
                      {result.status === 'success' ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <CheckCircle2 size={14} /> Success
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400">
                          <XCircle size={14} /> Failed
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-300">
                      {result.metrics?.accuracy ? `${(result.metrics.accuracy * 100).toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-300">
                      {result.metrics?.precision ? `${(result.metrics.precision * 100).toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-300">
                      {result.metrics?.recall ? `${(result.metrics.recall * 100).toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-300">
                      {result.metrics?.f1_score ? `${(result.metrics.f1_score * 100).toFixed(1)}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-red-400 text-xs max-w-xs truncate" title={result.error}>
                      {result.error || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {trainingResults.completed_at && (
            <div className="mt-4 text-xs text-gray-500">
              Completed: {new Date(trainingResults.completed_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Pipeline Status */}
      {pipelineHealth && (
        <>
          {/* Overall Status Card */}
          <div className={`rounded-lg p-6 border ${getStatusBg(pipelineHealth.status)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white mb-1">Pipeline Status</h2>
                <p className={`text-2xl font-semibold capitalize ${getStatusColor(pipelineHealth.status)}`}>
                  {pipelineHealth.status}
                </p>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getStatusBg(pipelineHealth.status)}`}>
                {pipelineHealth.healthy ? (
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
            <MetricCard
              label="Models Loaded"
              value={pipelineHealth.models_loaded}
              color="primary"
            />
            <MetricCard
              label="Queue Depth"
              value={pipelineHealth.queue_depth}
              color={pipelineHealth.queue_depth < 100 ? 'success' : 'warning'}
            />
            <MetricCard
              label="Avg Latency"
              value={`${pipelineHealth.avg_latency_ms.toFixed(1)}ms`}
              color={pipelineHealth.avg_latency_ms < 100 ? 'success' : 'warning'}
            />
            <MetricCard
              label="Error Rate"
              value={formatters.formatPercentage(pipelineHealth.error_rate)}
              color={pipelineHealth.error_rate < 1 ? 'success' : pipelineHealth.error_rate < 5 ? 'warning' : 'error'}
            />
          </Box>

          {/* Pipeline Details */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-4">Pipeline Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-400 mb-1">Tenant ID</div>
                <div className="text-white font-mono text-sm">{pipelineHealth.tenant_id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Last Prediction</div>
                <div className="text-white text-sm">
                  {pipelineHealth.last_prediction ? 
                    new Date(pipelineHealth.last_prediction).toLocaleString() : 
                    'No predictions yet'
                  }
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Pipeline Health</div>
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    pipelineHealth.healthy ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  <span className="text-white text-sm">
                    {pipelineHealth.healthy ? 'Operational' : 'Degraded'}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Last Updated</div>
                <div className="text-white text-sm">
                  {new Date(pipelineHealth.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Performance Thresholds */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-4">Performance Thresholds</h2>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Latency Target</span>
                  <span className={pipelineHealth.avg_latency_ms < 100 ? 'text-green-400' : 'text-yellow-400'}>
                    {pipelineHealth.avg_latency_ms < 100 ? 'Meeting Target' : 'Above Target'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">Target: &lt;100ms</div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Error Rate Target</span>
                  <span className={pipelineHealth.error_rate < 1 ? 'text-green-400' : 'text-red-400'}>
                    {pipelineHealth.error_rate < 1 ? 'Meeting Target' : 'Above Target'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">Target: &lt;1%</div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Queue Depth Target</span>
                  <span className={pipelineHealth.queue_depth < 100 ? 'text-green-400' : 'text-yellow-400'}>
                    {pipelineHealth.queue_depth < 100 ? 'Normal' : 'High'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">Target: &lt;100 items</div>
              </div>
            </div>
          </div>
        </>
      )}
    </Box>
  );
};

export default TrainingPipeline;
